import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, from, Observable } from 'rxjs';
import { environment } from 'src/app/environments/environment';

interface CredentialPublicKeyResponse {
  keyId: string;
  algorithm: 'RSA-OAEP-256';
  publicKey: string;
}

/**
 * Encrypted credential value and the public key id used to encrypt it.
 */
export interface EncryptedCredential {
  encryptedValue: string;
  keyId: string;
}

interface ImportedCredentialKey {
  keyId: string;
  cryptoKey: CryptoKey;
}

/**
 * Encrypts sensitive credential values before they are sent to eCitation
 * Services.
 *
 * The backend exposes a public RSA-OAEP key. This service imports and caches
 * that public key, encrypts passwords with Web Crypto, and returns only the
 * ciphertext plus key id to callers.
 */
@Injectable({
  providedIn: 'root'
})
export class CredentialEncryptionService {

  private importedCredentialKey?: ImportedCredentialKey;
  private readonly publicKeyUrl = `${environment.apiUrl}/ecitation-services/credential-public-key`;

  constructor(private http: HttpClient) { }

  /**
   * Encrypts a credential value with the backend's configured public key.
   *
   * @param plaintext credential text from the browser form
   * @param forceRefresh when true, fetches the latest public key before encrypting
   * @returns observable ciphertext and key id
   */
  encryptCredential(plaintext: string, forceRefresh = false): Observable<EncryptedCredential> {
    return from(this.encryptCredentialAsync(plaintext, forceRefresh));
  }

  /**
   * Clears the cached imported public key so the next encryption uses fresh
   * backend key metadata.
   */
  clearCachedKey() {
    this.importedCredentialKey = undefined;
  }

  /**
   * Performs public-key import and RSA-OAEP encryption.
   *
   * @param plaintext credential text from the browser form
   * @param forceRefresh when true, bypasses the cached imported key
   * @returns encrypted credential payload
   */
  private async encryptCredentialAsync(plaintext: string, forceRefresh: boolean): Promise<EncryptedCredential> {
    const importedKey = await this.getImportedKey(forceRefresh);
    const encodedPlaintext = new TextEncoder().encode(plaintext);
    const plaintextBuffer = new ArrayBuffer(encodedPlaintext.byteLength);
    new Uint8Array(plaintextBuffer).set(encodedPlaintext);
    const encryptedBytes = await window.crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      importedKey.cryptoKey,
      plaintextBuffer
    );

    return {
      encryptedValue: this.arrayBufferToBase64(encryptedBytes),
      keyId: importedKey.keyId
    };
  }

  /**
   * Gets the cached public key or fetches and imports a new one from the
   * backend.
   *
   * @param forceRefresh when true, bypasses the cached key
   * @returns imported Web Crypto key and key id
   */
  private async getImportedKey(forceRefresh: boolean): Promise<ImportedCredentialKey> {
    if (this.importedCredentialKey && !forceRefresh) {
      return this.importedCredentialKey;
    }

    const keyResponse = await firstValueFrom(this.http.get<CredentialPublicKeyResponse>(this.publicKeyUrl));
    const keyBytes = this.base64ToArrayBuffer(keyResponse.publicKey);
    const cryptoKey = await window.crypto.subtle.importKey(
      'spki',
      keyBytes,
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false,
      ['encrypt']
    );

    this.importedCredentialKey = {
      keyId: keyResponse.keyId,
      cryptoKey
    };

    return this.importedCredentialKey;
  }

  /**
   * Converts a Base64 encoded key into bytes for Web Crypto import.
   *
   * @param value Base64 encoded value
   * @returns decoded bytes
   */
  private base64ToArrayBuffer(value: string): ArrayBuffer {
    const binary = window.atob(value);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index++) {
      bytes[index] = binary.charCodeAt(index);
    }

    return bytes.buffer;
  }

  /**
   * Converts encrypted bytes into Base64 for transport in JSON.
   *
   * @param value encrypted bytes
   * @returns Base64 encoded ciphertext
   */
  private arrayBufferToBase64(value: ArrayBuffer): string {
    const bytes = new Uint8Array(value);
    let binary = '';

    for (let index = 0; index < bytes.byteLength; index++) {
      binary += String.fromCharCode(bytes[index]);
    }

    return window.btoa(binary);
  }
}
